/**
 * The trains found, three cards side by side as on the old kiosk (339×595, 8 px apart). The page is the
 * backend's (`page_idx`), so "show me the next ones" by voice and the main-bar arrows move the same
 * list, and so does a swipe, the row following the finger. A tap on a card picks the train, a tap on a price picks train and class (and fare, if several).
 */
import { useState } from 'react';
import { formatMoney } from '@platform/i18n/translator';
import { Icon } from '@platform/ui-kit/Icon';
import { useSwipe } from '@platform/ui-kit/swipe';
import { defineWidget, type WidgetApi } from '@platform/widgets/contract';
import { classRows, durationLabel, trainTypeLabel, type ClassRow } from '../fares';
import type { Train } from '../session';
import { RESULTS_PER_PAGE, trainResultsSchema } from './results-schema';
import css from './Trains.module.css';
import { TrainSingle } from './TrainSingle';
import { ChangesButton, ItineraryCover } from './TrainItinerary';
import { changesOf, trainItinerary } from './itinerary';

type T = (k: string, p?: Record<string, string | number>) => string;

export const trainResults = defineWidget({
  key: 'train-results',
  schema: trainResultsSchema,
  // the Essenziale look: one train at a time (TrainSingle.tsx)
  // its neighbours peek in from the screen's edges: no margin from the edges for it
  variants: [{ id: 'single', label: 'widget.variant.single', Component: TrainSingle, edgeToEdge: true }],
  Component: function TrainResults({ props, ctx, api }) {
    const { t, language } = ctx;
    const pages = Math.max(1, Math.ceil(props.trains.length / RESULTS_PER_PAGE));
    const page = Math.min(props.page, pages - 1);
    const money = (n: number) => formatMoney(n, language);
    // a swipe turns the backend's page, as the main bar's arrows do; the row follows the finger meanwhile
    const { area, moving, track } = useSwipe<HTMLDivElement>({
      enabled: api.swipe && !api.readOnly && pages > 1,
      follow: true,
      at: page,
      can: (direction) => (direction === 'next' ? page < pages - 1 : page > 0),
      onSwipe: (direction) => api.press(direction === 'next' ? 'go_next_page' : 'go_prev_page'),
    });

    const cards = (p: number) => (
      <div className={css.cardRow}>
        {props.trains.slice(p * RESULTS_PER_PAGE, (p + 1) * RESULTS_PER_PAGE).map((train, i) => {
          const idx = p * RESULTS_PER_PAGE + i + 1;
          return (
            <TrainCard key={`${train.train_id}-${train.departure_time}`} train={train} idx={idx}
              selected={props.selectedIdx === idx} dimmed={props.selectedIdx !== null && props.selectedIdx !== idx}
              money={money} t={t} api={api}
              onTrain={() => api.press('select_train', { idx })}
              onClass={(row, fare) => api.press('select_class', fare ? { idx, class: row.name, fare } : { idx, class: row.name })} />
          );
        })}
      </div>
    );
    // the pages beside, only while the row is off its rest: seen as the finger pulls them in, never touched
    const beside = (p: number, side: 'before' | 'after') => moving && p >= 0 && p < pages && (
      <div className={`${css.besidePage} ${css[side]}`} aria-hidden inert>{cards(p)}</div>
    );

    if (props.trains.length === 0) return <div className={css.noTrains}>{t('trains.results.empty')}</div>;
    return (
      <div className={css.results} {...area}>
        <div className={css.pager}>
          <div className={css.track} ref={track}>
            {cards(page)}
            {beside(page - 1, 'before')}
            {beside(page + 1, 'after')}
          </div>
        </div>
        {pages > 1 && (
          <div className={css.dots} role="img" aria-label={t('trains.results.page', { page: page + 1, pages })}>
            {Array.from({ length: pages }, (_, p) => <i key={p} className={p === page ? css.dotOn : undefined} />)}
          </div>
        )}
      </div>
    );
  },
});

function TrainCard({ train, idx, selected, dimmed, money, t, api, onTrain, onClass }: {
  train: Train;
  idx: number;
  selected: boolean;
  dimmed: boolean;
  money: (n: number) => string;
  t: T;
  api: WidgetApi;
  onTrain: () => void;
  onClass: (row: ClassRow, fare?: string) => void;
}) {
  const [openClass, setOpenClass] = useState<string | null>(null);
  const rows = classRows(train.class_prices);
  const blocked = train.blocked_reason ?? (train.available_seats === 0 ? 'sold_out' : null);
  return (
    <article className={`${css.card} ${selected ? css.selected : ''} ${dimmed ? css.dimmed : ''} ${blocked ? css.blocked : ''}`}>
      <button className={css.cardHead} onClick={onTrain} disabled={train.available_seats === 0}>
        <span className={css.position}>{idx}°</span>
        <Icon name="more" size={20} />
        <Icon name="train" size={20} />
        <span className={css.number}>{train.train_id}</span>
        {selected && <Icon name="sparkles" size={22} />}
      </button>
      <button className={css.timeChip} onClick={onTrain} disabled={train.available_seats === 0}>
        <Icon name="clock" size={22} />
        <span className={css.time}><strong>{train.departure_time}</strong><small>{t('trains.results.departure')}</small></span>
        <Icon name="next" size={18} />
        <span className={css.time}><strong>{train.arrival_time}</strong><small>{t('trains.results.arrival')}</small></span>
      </button>
      <div className={css.cardBody}>
        <div className={css.info}>
          <p><Icon name="train" size={20} /><span><b>{trainTypeLabel(train, t('trains.results.high_speed'))}</b><small>{durationLabel(train.duration_min)}{changesOf(train) === 0 && ` · ${t('trains.results.direct')}`}</small></span></p>
          <p><Icon name="map" size={20} /><span><b>{train.departure_station} → {train.arrival_station}</b></span></p>
          {changesOf(train) > 0 && <p><ChangesButton of={trainItinerary(train, idx)} api={api} t={t} look="row" /></p>}
          <p><Icon name="seat" size={20} /><span><b>{t('trains.results.seats', { n: train.available_seats })}</b></span></p>
        </div>
        <div className={css.prices}>
          <p className={css.pricesTitle}><Icon name="ticket" size={18} />{t('trains.results.prices')}</p>
          {rows.slice(0, 5).map((row) => (
            <div key={row.name} className={css.classCell}>
              <button className={css.classButton} disabled={!row.available}
                onClick={() => (row.tiers.length > 0 ? setOpenClass((o) => (o === row.name ? null : row.name)) : onClass(row))}>
                <span className={css.className}>{row.name}</span>
                <span className={css.price}>
                  {row.available ? `${row.fromPrice ? `${t('trains.results.from')} ` : ''}${money(row.price)}` : t('trains.results.sold_out')}
                </span>
              </button>
              {openClass === row.name && (
                <div className={css.tiers}>
                  {row.tiers.map((tier) => (
                    <button key={tier.fare} className={css.tier} disabled={!tier.available} onClick={() => onClass(row, tier.fare)}>
                      <span>{tier.fare}</span><strong>{tier.available ? money(tier.price) : t('trains.results.sold_out')}</strong>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {blocked && <p className={css.blockedNote}>{t(`trains.results.blocked.${blocked}`)}</p>}
      <ItineraryCover of={trainItinerary(train, idx)} t={t} closeLabel={t('overlay.close')} look="cards" headerClassName={css.coverHead} />
    </article>
  );
}
